import React, { useCallback, useMemo } from 'react';
import { LoadingBar } from 'ui/NetworkStatusBar';
import { ENetworkRequestStatus } from 'services/BackendApi/types/Generic';
import { useDispatch, useSelector } from 'react-redux';
import * as BreakdownSelectors from 'store/modules/bookingManager/subdomains/breakdown/selectors';
import * as BreakdownActions from 'store/modules/bookingManager/subdomains/breakdown/actions';
import { GeneralModal } from 'ui/GeneralModal';
import { ModalHeading } from 'ui/GeneralModal/ModalHeading';
import { getCurrencyBySymbol } from 'utils';
import { RHPOffers } from '../AddAccommodationModal/RightHandPanel/RHPOffers';
import { RHPServiceCharge } from '../AddAccommodationModal/RightHandPanel/RHPServiceCharge';
import { AddAncillaryRightHandPanelTotalCost } from 'ui/AddAncillarySharedComponents/AddAncillaryRightHandPanelTotalCost';
import FluidButton from 'ui/FluidButton';
import { IStaticRate } from '../AddAccommodationModal/MealPlanDropdown/types';
import { convertMealPlanToStaticRate } from '../AddAccommodationModal/helpers';
import classNames from 'classnames';
import { PeopleAndDatesInputs } from './inputs/PeopleAndDatesInputs';
import { MealPlanAndOccasionInputs } from './inputs/MealPlanAndOccasionInputs';
import { IsRepeatGuestInput } from './inputs/IsRepeatGuestInput';
import { getBootstrapExtraPersonSupplementProductSelector } from 'store/modules/bootstrap/selectors';

export interface IEditAccommodationModalProps {
  bookingUuid: string;
  bookingCurrency: string;
}

export const EditAccommodationModal = ({ bookingCurrency, bookingUuid }: IEditAccommodationModalProps) => {
  const dispatch = useDispatch();
  const guestAges = useSelector(BreakdownSelectors.editAccommodationGuestAgesSelector);
  const selectedDates = useSelector(BreakdownSelectors.editAccommodationSelectedDatesSelector);
  const occasions = useSelector(BreakdownSelectors.editAccommodationOccasionsSelector);
  const selectedMealPlan = useSelector(BreakdownSelectors.editAccommodationSelectedMealPlanSelector);
  const isRepeatGuest = useSelector(BreakdownSelectors.editAccommodationIsRepeatGuestSelector);
  const accommodationDetails = useSelector(BreakdownSelectors.editAccommodationAccommodationDetailsSelector);
  const selectedBuild = useSelector(BreakdownSelectors.editAccommodationSelectedBuildSelector);
  const initiateBookingBuildRequest = useSelector(
    BreakdownSelectors.editAccommodationInitiateBookingBuildRequestSelector
  );
  const bookingBuildRequest = useSelector(BreakdownSelectors.editAccommodationBookingBuildRequestSelector);
  const lastRequestedBuild = useSelector(BreakdownSelectors.editAccommodationLastRequestedBuildSelector);
  const headlineLineItemBreakdown = useSelector(BreakdownSelectors.headlineLineItemBreakdownSelector);
  const countryData = useSelector(BreakdownSelectors.editAccommodationCountryDataSelector);
  const simulateBreakdownRequest = useSelector(BreakdownSelectors.editAccommodationSimulateBreakdownRequestSelector);
  const errors = useSelector(BreakdownSelectors.editAccommodationErrorsSelector);
  const hasEdited = useSelector(BreakdownSelectors.editAccommodationHasEditedSelector);
  const isManualEditMode = useSelector(BreakdownSelectors.editAccommodationIsManualEditModeSelector);
  const allHotelMealPlans = useSelector(BreakdownSelectors.editAccommodationAllHotelMealPlansSelector);

  const errorsWarnings = useMemo(() => errors?.warnings || [], [errors?.warnings]);
  const errorsStops = useMemo(() => errors?.stops || [], [errors?.stops]);
  const mealPlanOptions: IStaticRate[] = useMemo(() => {
    // In manual edit mode, use all hotel meal plans converted to IStaticRate format
    if (isManualEditMode && allHotelMealPlans.length > 0) {
      return allHotelMealPlans.map(product => ({
        mealPlan: {
          uuids: [product.uuid],
          names: [product.name],
          categoryName: product.category,
          isOnRequest: false,
          totalBeforeDiscountCents: undefined, // No pricing in manual mode
          totalCents: undefined, // No pricing in manual mode
        },
        offers: [],
        cancellationPolicy: '',
        isOnRequest: false,
        totalBeforeDiscountCents: undefined,
        totalCents: undefined,
        isRefundable: null,
      }));
    }
    // Normal mode: use selectedBuild meal plans
    return (
      selectedBuild?.availableProductSets.Accommodation[0].availableSubProductSets['Meal Plan'].map(item =>
        convertMealPlanToStaticRate(item)
      ) || []
    );
  }, [isManualEditMode, allHotelMealPlans, selectedBuild]);
  const disableForm = useMemo(() => {
    // In manual edit mode, don't disable the form even with errors
    if (isManualEditMode) {
      return bookingBuildRequest === ENetworkRequestStatus.PENDING;
    }
    // Normal mode: disable form for errors and booking build failures
    return (
      bookingBuildRequest === ENetworkRequestStatus.PENDING ||
      initiateBookingBuildRequest === ENetworkRequestStatus.ERROR ||
      errorsStops.length > 0
    );
  }, [isManualEditMode, bookingBuildRequest, errorsStops, initiateBookingBuildRequest]);
  const disableSaveButton = useMemo(() => {
    // Base conditions
    if (disableForm || !hasEdited) return true;

    // In manual edit mode, also require meal plan selection
    if (isManualEditMode && !selectedMealPlan) return true;

    return false;
  }, [disableForm, hasEdited, isManualEditMode, selectedMealPlan]);

  const handleCloseEditAccommodationModal = useCallback(() => {
    dispatch(BreakdownActions.editAccommodationCloseModalAction());
  }, [dispatch]);

  const handleSaveAccommodationButton = useCallback(async () => {
    if (isManualEditMode) {
      // Manual save mode - directly update breakdown without API calls
      dispatch(BreakdownActions.editAccommodationManualSaveRequestAction(bookingUuid));
    } else {
      // Normal save mode - use simulate breakdown API
      if (!headlineLineItemBreakdown || !selectedBuild || !lastRequestedBuild || !countryData) {
        return;
      }
      dispatch(BreakdownActions.editAccommodationSimulateBreakdownRequestAction(bookingUuid));
    }
  }, [
    dispatch,
    isManualEditMode,
    bookingUuid,
    headlineLineItemBreakdown,
    selectedBuild,
    lastRequestedBuild,
    countryData,
  ]);

  const handleCancelAccommodationButton = useCallback(() => {
    dispatch(BreakdownActions.editAccommodationCloseModalAction());
  }, []);

  if (!guestAges || initiateBookingBuildRequest === ENetworkRequestStatus.IDLE) {
    return null;
  }

  if (initiateBookingBuildRequest === ENetworkRequestStatus.PENDING) {
    return (
      <GeneralModal
        isOpen={true}
        onClose={handleCloseEditAccommodationModal}
        isCloseButtonVisible={true}
        modalWindowClassName="edit-accommodatiom-modal-container font-pt-sans min-w-[650px] min-h-[590px] px-[34px] py-[25px] rounded !overflow-visible"
      >
        <ModalHeading>Edit Accommodation</ModalHeading>
        <div className="mt-4">
          <LoadingBar />
        </div>
      </GeneralModal>
    );
  }

  return (
    <GeneralModal
      isOpen={true}
      onClose={handleCloseEditAccommodationModal}
      isCloseButtonVisible={true}
      modalWindowClassName="edit-accommodatiom-modal-container font-pt-sans min-w-[650px] min-h-[590px] flex flex-col px-[34px] py-[25px] rounded !overflow-visible"
    >
      <ModalHeading>Edit Accommodation</ModalHeading>
      {isManualEditMode ? (
        <div className="bg-yellow-10 border border-yellow-40 p-3 mb-4 rounded mt-4">
          <div className="text-yellow-60 text-sm">
            We cannot check rates in the past, please make sure to update them manually in breakdown.
          </div>
        </div>
      ) : (
        (errorsStops.length > 0 || initiateBookingBuildRequest === ENetworkRequestStatus.ERROR) && (
          <div className="text-red-95 mt-4">
            This room can't be edited. Please, review the information with the System Admin.
          </div>
        )
      )}
      <div className="flex-1 mt-4">
        <div className="hotel-and-room-name mb-[25px]">
          <p className="hotel-name font-bold text-[17px] m-0 pb-[5px]">{accommodationDetails?.hotelNames.join(', ')}</p>
          <p className="room-name text-[15px] m-0">{accommodationDetails?.roomName}</p>
        </div>
        <div
          className={classNames('inputs', {
            'opacity-50':
              bookingBuildRequest === ENetworkRequestStatus.PENDING ||
              bookingBuildRequest === ENetworkRequestStatus.ERROR ||
              initiateBookingBuildRequest === ENetworkRequestStatus.ERROR ||
              simulateBreakdownRequest === ENetworkRequestStatus.PENDING ||
              (!isManualEditMode && errorsStops.length > 0),
          })}
        >
          <PeopleAndDatesInputs
            disabled={disableForm}
            guestAges={guestAges}
            selectedDates={selectedDates}
            isManualEditMode={isManualEditMode}
          />
          <MealPlanAndOccasionInputs
            disabled={disableForm}
            bookingBuildRequest={bookingBuildRequest}
            bookingCurrency={bookingCurrency}
            mealPlanOptions={mealPlanOptions}
            occasions={occasions}
            selectedMealPlan={selectedMealPlan}
            isManualEditMode={isManualEditMode}
          />
          {!isManualEditMode && <IsRepeatGuestInput disabled={disableForm} isRepeatGuest={isRepeatGuest} />}
          {selectedBuild && !isManualEditMode && (
            <div className="bottom-part space-y-10px pt-10px">
              <RHPOffers selectedBuild={selectedBuild} />

              <RHPServiceCharge
                currencySymbol={getCurrencyBySymbol(bookingCurrency || '').symbol}
                selectedBuild={selectedBuild}
              />

              {errorsWarnings.map((warning, index) => (
                <p key={index} className="text-yellow-60 text-xs mt-2">
                  {warning}
                </p>
              ))}

              {!disableSaveButton && (
                <p className="text-yellow-60 text-xs mt-2">
                  Any manually edited prices in the breakdown will be replaced with the latest available data
                </p>
              )}
            </div>
          )}
        </div>
      </div>
      {selectedBuild && !isManualEditMode && (
        <AddAncillaryRightHandPanelTotalCost
          price={selectedBuild.totals.total}
          isOnRequest={selectedBuild.totals.oneOrMoreItemsOnRequest}
          currencySymbol={getCurrencyBySymbol(bookingCurrency || '').symbol}
        />
      )}
      <div className="buttons flex gap-5 w-[130px] !mt-[20px]">
        <FluidButton onClick={handleCancelAccommodationButton} className="block w-full" type="secondary">
          Cancel
        </FluidButton>
        <FluidButton
          disabled={disableSaveButton}
          isLoading={simulateBreakdownRequest === ENetworkRequestStatus.PENDING}
          onClick={handleSaveAccommodationButton}
          className="block w-full"
          type="primary"
        >
          Save
        </FluidButton>
      </div>
    </GeneralModal>
  );
};
